import React from 'react';
import validator from 'validator';
import styles from './Signup.module.css';
import * as utils from '../../utils/utils';

export default class Signup extends React.Component {

    constructor(props) {
        super(props);

        this.screens = {
            Credentials: 1,
            Otp: 2,
        };

        this.state = {
            currentScreen: this.screens.Credentials,

            email: '',
            inviteCode: '',
            uniqueKey: '',
            otp: '',

            isLoading: false,
            errorText: '',
            infoText: 'Use your invite to unlock your account in under a minute.',
        };
    }

    onInputChange = (event) => {
        const {
            name,
            value,
        } = event.target;

        this.setState({
            [name]: value,
            errorText: '',
        });
    };

    getErrorMessage = (error, fallbackText) => {
        if (error && typeof error === 'object' && typeof error.message === 'string' && error.message.trim() !== '') {
            return error.message;
        }

        if (typeof error === 'string' && error.trim() !== '') {
            return error;
        }

        return fallbackText;
    };

    requestOtp = () => {
        const email = this.state.email.trim();
        const inviteCode = this.state.inviteCode.trim();

        if (!validator.isEmail(email)) {
            this.setState({ errorText: 'Please enter a valid email address.' });
            return;
        }

        // if (inviteCode.length < 3) {
        //     this.setState({ errorText: 'Please enter your invite code.' });
        //     return;
        // }

        this.setState({
            isLoading: true,
            errorText: '',
            infoText: 'Sending a one-time passcode...'
        }, () => {
            utils.sendRequest('/auth/signin', {
                email,
                inviteCode,
            })
                .then((responseData) => {
                    const uniqueKey = (responseData && responseData.uniqueKey) ? `${responseData.uniqueKey}`.trim() : '';

                    if (uniqueKey === '') {
                        throw new Error('Could not start sign in right now. Please try again.');
                    }

                    this.setState({
                        isLoading: false,
                        uniqueKey,
                        currentScreen: this.screens.Otp,
                        infoText: `We sent a one-time passcode to ${email}.`,
                    });
                })
                .catch(error => {
                    console.log('signin error', error);
                    this.setState({
                        isLoading: false,
                        errorText: this.getErrorMessage(error, 'Could not start sign in. Please check your invite code and try again.'),
                        infoText: 'Use your invite to unlock your account in under a minute.',
                    });
                });
        });
    };

    verifyOtp = () => {
        const otp = this.state.otp.trim();
        const email = this.state.email.trim();
        const uniqueKey = this.state.uniqueKey.trim();

        if (otp.length < 4) {
            this.setState({ errorText: 'Please enter the OTP from your email.' });
            return;
        }

        if (uniqueKey === '') {
            this.setState({
                errorText: 'Session expired. Please request a new OTP.',
                currentScreen: this.screens.Credentials,
                otp: '',
                infoText: 'Use your invite to unlock your account in under a minute.',
            });
            return;
        }

        this.setState({
            isLoading: true,
            errorText: '',
            infoText: 'Verifying your passcode...'
        }, () => {
            utils.sendRequest('/auth/verifyOtp', {
                email,
                uniqueKey,
                otp,
            })
                .then(responseData => {
                    const {
                        isNewUser,
                        userID,
                        userUniqueID,
                    } = responseData || {};

                    if (userID !== undefined && userUniqueID !== undefined) {
                        utils.setAuthDetails(userID, userUniqueID);
                        utils.setUserInfo({ email });

                        if ( isNewUser ) {
                            window.location.href = '/onboarding';
                        }
                        else {
                            window.location.href = '/dashboard';
                        }
                        return;
                    }

                    throw new Error('Could not verify OTP. Please request a new code.');
                })
                .catch(error => {
                    console.log('verify otp error', error);
                    this.setState({
                        isLoading: false,
                        errorText: this.getErrorMessage(error, 'Invalid OTP. Please try again.'),
                        infoText: `We sent a one-time passcode to ${email}.`,
                    });
                });
        });
    };

    goBackToCredentials = () => {
        this.setState({
            currentScreen: this.screens.Credentials,
            uniqueKey: '',
            otp: '',
            errorText: '',
            infoText: 'Use your invite to unlock your account in under a minute.',
        });
    };

    renderCredentialsScreen = () => {
        const {
            email,
            inviteCode,
            isLoading,
        } = this.state;

        return (
            <div className={styles.form}>
                <div className={styles.fieldGroup}>
                    <label className={styles.label} htmlFor='signup-email'>Email</label>
                    <input
                        id='signup-email'
                        name='email'
                        type='email'
                        autoFocus
                        autoComplete='email'
                        placeholder='you@company.com'
                        className={styles.input}
                        value={email}
                        onChange={this.onInputChange}
                        onKeyDown={this.handleCredentialKeyDown}
                    />
                </div>

                <div className={styles.fieldGroup}>
                    <label className={styles.label} htmlFor='invite-code'>Invite Code</label>
                    <input
                        id='invite-code'
                        name='inviteCode'
                        type='text'
                        autoComplete='one-time-code'
                        placeholder='INVITE-2026'
                        className={styles.input}
                        value={inviteCode}
                        onChange={this.onInputChange}
                        onKeyDown={this.handleCredentialKeyDown}
                    />
                </div>

                <button className={styles.primaryButton} onClick={this.requestOtp} disabled={isLoading}>
                    {isLoading ? 'Preparing OTP...' : 'Sign In'}
                </button>
            </div>
        );
    };

    handleCredentialKeyDown = (e) => {
        // Only trigger if exactly 'Enter' is pressed inside the active input
        if (e.key === 'Enter') {
            this.requestOtp();
        }
    };

    handleOtpKeyDown = (e) => {
        // Only trigger if exactly 'Enter' is pressed inside the active input
        if (e.key === 'Enter') {
            this.verifyOtp();
        }
    };

    renderOtpScreen = () => {
        const {
            otp,
            isLoading,
        } = this.state;

        return (
            <div className={styles.form}>
                <div className={styles.fieldGroup}>
                    <label className={styles.label} htmlFor='otp'>One-Time Passcode</label>
                    <input
                        id='otp'
                        name='otp'
                        type='text'
                        inputMode='numeric'
                        ref={(input) => { if (input) input.focus(); }}
                        autoComplete='one-time-code'
                        placeholder='Enter OTP'
                        className={styles.input}
                        value={otp}
                        onChange={this.onInputChange}
                        onKeyDown={this.handleOtpKeyDown}
                    />
                </div>

                <button className={styles.primaryButton} onClick={this.verifyOtp} disabled={isLoading}>
                    {isLoading ? 'Verifying...' : 'Verify OTP'}
                </button>

                <button
                    className={styles.secondaryButton}
                    type='button'
                    onClick={this.goBackToCredentials}
                    disabled={isLoading}
                >
                    Back
                </button>
            </div>
        );
    };

    renderCurrentScreen = () => {
        const {
            currentScreen,
        } = this.state;

        if (currentScreen === this.screens.Credentials) {
            return this.renderCredentialsScreen();
        }
        else if (currentScreen === this.screens.Otp) {
            return this.renderOtpScreen();
        }
    };

    render() {
        const {
            errorText,
            infoText,
        } = this.state;

        return (
            <div className={styles.pageContainer}>
                <div className={styles.glowTop} />
                <div className={styles.glowBottom} />

                <div className={styles.layoutShell}>
                    <section className={styles.leftPanel}>
                        <div className={styles.brandBadge}>HQVibe</div>
                        <h1 className={styles.title}>Build a trusted profile that opens doors.</h1>
                        <p className={styles.subtitle}>
                            Designed for high-context hiring and verified work proof. Fast onboarding, private by default.
                        </p>

                        <div className={styles.userRow}>
                            <span className={styles.userCaption}>Trusted by early beta members</span>
                        </div>

                        {/* <a
                            href='https://apps.apple.com/'
                            target='_blank'
                            rel='noreferrer'
                            className={styles.appStoreLink}
                        >
                            <img src={appStoreBadge} alt='Download on the App Store' className={styles.appStoreBadge} />
                        </a> */}
                    </section>

                    <section className={styles.cardPanel}>
                        <div className={styles.stepRow}>
                            <div className={styles.stepDot} />
                            <div className={styles.stepLine} />
                            <div className={`${styles.stepDot} ${this.state.currentScreen === this.screens.Otp ? styles.stepDotActive : ''}`} />
                        </div>

                        <h2 className={styles.formTitle}>
                            {this.state.currentScreen === this.screens.Credentials ? 'Secure Sign In' : 'Confirm OTP'}
                        </h2>
                        <p className={styles.formText}>{infoText}</p>

                        {this.renderCurrentScreen()}

                        {
                            errorText !== '' &&
                            <div className={styles.errorBox}>{errorText}</div>
                        }
                    </section>
                </div>
            </div>
        );
    }
}
